/**
 * Where a name typed (or tapped) on Explore leads.
 *
 * One decision, one implementation. Explore has two entries that resolve a
 * name — the hero search box and the trending strip — and until now only the
 * search box actually resolved anything: trending string-matched the name
 * against whichever community plans happened to be loaded and, with no else
 * branch, did nothing when none did. Fixing that by writing a second ordering
 * beside the first is how the two drift; the ordering lives here instead, and
 * both call it.
 *
 * The order is deliberate: **a place outranks a plan.** Someone typing
 * "Granada" wants Granada, not whichever itinerary happens to mention it. A
 * full miss ends at Ask AI, which is the §22.4 generate-on-miss entry.
 */
import { DestinationCard, fetchExploreSearch } from '../services/atlasApi';

export type ExploreTarget =
  | { kind: 'destination'; slug: string }
  | { kind: 'plan'; slug: string }
  | { kind: 'ask_ai'; query: string };

/** Community plans whose title or destination mentions `query`. */

/** The decision itself, given destinations already in hand.
 *
 *  The plan parameter narrowed to what the decision reads: slice 4c moved the
 *  search box onto the server endpoint, whose plan rows are not
 *  `CommunityPlanListItem`s — and the choice only ever needed a slug. */
export function chooseExploreTarget(
  destinations: Pick<DestinationCard, 'slug'>[],
  plans: { slug: string }[],
  query: string,
): ExploreTarget {
  if (destinations.length > 0) return { kind: 'destination', slug: destinations[0].slug };
  if (plans.length > 0) return { kind: 'plan', slug: plans[0].slug };
  return { kind: 'ask_ai', query: query.trim() };
}

/**
 * The same decision for a caller that has only a name — it does the atlas
 * lookup first.
 *
 * A failed lookup resolves to "no destinations" rather than rejecting: the tap
 * must still land somewhere, and the fallbacks below it are exactly what a
 * genuine miss gets.
 *
 * No `destination_searched` event fires here. The event describes what someone
 * TYPED (§22.6: outcome, type and length, never the text); a tap on a strip is
 * not a search, and counting it as one would move the miss rate without anyone
 * having searched. The server's own miss log does see the lookup — a trending
 * destination the atlas cannot resolve is a genuine gap in the atlas, which is
 * what that log is for.
 */
export async function resolveExploreTarget(query: string): Promise<ExploreTarget> {
  // The unified endpoint (slice 4c): destinations and plans in one answer, so
  // the fallback plan is any published plan and not just the ones the calling
  // page happened to have loaded.
  let destinations: Pick<DestinationCard, 'slug'>[] = [];
  let plans: { slug: string }[] = [];
  try {
    const found = await fetchExploreSearch(query);
    // `?? []`, because a malformed answer slides through the catch — the keys
    // being absent throws nothing — and `.length` then blows up in a click
    // handler whose whole contract is that the tap still lands somewhere.
    destinations = found.destinations ?? [];
    plans = found.plans ?? [];
  } catch {
    destinations = [];
  }
  return chooseExploreTarget(destinations, plans, query);
}
